/**
 * =============================================================================
 * IDS CRM Web - Jobs & Tickets Hub
 * =============================================================================
 *
 * PURPOSE:
 * Central operational work list for reactive tickets, scheduled engineer work,
 * and scheduled maintenance.
 *
 * =============================================================================
 */

import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { isFieldEngineer, getCapabilities, canSeeWorkBoard } from '../utils/permissions';
import WorkBoard from '../components/WorkBoard';
import EngineerBoard from '../components/EngineerBoard';
import { formatDateKey } from '../utils/dates';
import { useListMemory, useRememberList } from '../hooks/useListMemory';
import './Tickets.css';

const WORK_TABS = [
  { value: 'all', label: 'All Work' },
  { value: 'reactive_ticket', label: 'Reactive Tickets' },
  { value: 'scheduled_work', label: 'Scheduled Work' },
  { value: 'scheduled_maintenance', label: 'Scheduled Maintenance' }
];

// Fallbacks — the live filter options come from the configured support lists
// (status lists grouped by work type + comms channels), active values only.
const STATUS_GROUPS = [
  ['reactive_ticket_statuses', 'Reactive Tickets'],
  ['scheduled_work_statuses', 'Scheduled Work'],
  ['scheduled_maintenance_statuses', 'Scheduled Maintenance']
];

const FALLBACK_STATUS_GROUPS = [
  {
    label: 'Reactive Tickets',
    options: ['open', 'in_progress', 'awaiting_customer', 'awaiting_parts', 'escalated', 'closed']
      .map(value => ({ value, label: value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }))
  },
  {
    label: 'Scheduled Work',
    options: ['suggested', 'scheduled', 'confirmed', 'complete', 'cancelled']
      .map(value => ({ value, label: value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }))
  },
  {
    label: 'Scheduled Maintenance',
    options: ['overdue', 'completed']
      .map(value => ({ value, label: value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) }))
  }
];

const FALLBACK_CHANNEL_OPTIONS = [
  { value: 'phone', label: 'Phone' },
  { value: 'whatsapp', label: 'WhatsApp' },
  { value: 'email', label: 'Email' },
  { value: 'portal', label: 'Portal' }
];

// Grouped by work type — the vocabularies overlap in label ("Completed" is
// scheduled work's 'complete' AND maintenance's 'completed'), so a flat merge
// shows confusing duplicates; groups keep each value under its own work type.
const buildStatusGroups = (data) => {
  const groups = STATUS_GROUPS.map(([key, label]) => ({
    label,
    options: (data?.[key] || [])
      .filter(item => item.active !== false)
      .map(({ value, label: optionLabel }) => ({ value, label: optionLabel }))
  })).filter(group => group.options.length > 0);
  return groups.length > 0 ? groups : FALLBACK_STATUS_GROUPS;
};

// Columns that sort when their header is pressed (Gareth 2026-09-23): the
// API sort key and the header text. Action is not sortable.
const SORT_COLUMNS = [
  ['ref', 'Ref'],
  ['type', 'Type'],
  ['work', 'Work'],
  ['customer', 'Customer'],
  ['asset', 'Asset'],
  ['priority', 'Priority'],
  ['status', 'Status'],
  ['date', 'Date'],
  ['assigned', 'Assigned']
];
const SORT_KEYS = SORT_COLUMNS.map(([key]) => key);

// Each press moves a column on: ascending, then descending, then back to the
// usual order (overdue first, then by date).
const nextSort = (current, key) => {
  if (!current || current.key !== key) return { key, dir: 'asc' };
  return current.dir === 'asc' ? { key, dir: 'desc' } : null;
};

function Tickets() {
  const { authFetch, user } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  // Coming back from a job or ticket (a "←" link or the browser's Back)
  // shows the list as it was left; a fresh visit starts from the address.
  const memory = useListMemory('/tickets');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState(() => ({ page: memory.initial('page', 1), total: 0, totalPages: 0 }));
  const [typeCounts, setTypeCounts] = useState({});
  const [search, setSearch] = useState(() => memory.initial('search', searchParams.get('search') || ''));
  // A column sort lives in the address, so it is still there after opening a
  // job and pressing Back, and the sidebar's plain Jobs & Tickets link clears
  // it on screen as well as in the address.
  const sortKey = SORT_KEYS.includes(searchParams.get('sort')) ? searchParams.get('sort') : null;
  const sortDir = searchParams.get('dir') === 'desc' ? 'desc' : 'asc';
  const sort = useMemo(() => (sortKey ? { key: sortKey, dir: sortDir } : null), [sortKey, sortDir]);
  // Only the newest request may fill the list: a slower earlier reply (after
  // quick presses of Next or a header) is dropped instead of overwriting it.
  const latestRequest = useRef(0);
  const [activeTab, setActiveTab] = useState(() => memory.initial('tab', searchParams.get('work_type') || 'all'));
  const [statusFilter, setStatusFilter] = useState(() => memory.initial('status', searchParams.get('status') || ''));
  // Engineers default to their own work — it was their first click every
  // day (UX feedback 2026-08-03 #3). A ?my= URL param always wins.
  const [myWork, setMyWork] = useState(() => memory.initial(
    'my',
    searchParams.has('my')
      ? searchParams.get('my') === 'true'
      : isFieldEngineer(user?.role)
  ));

  // Ticket-only filters (fault category, channel, warranty, opened dates).
  // Setting any of them narrows results to reactive tickets server-side.
  const [faultCategory, setFaultCategory] = useState(() => memory.initial('faultCategory', ''));
  const [channelFilter, setChannelFilter] = useState(() => memory.initial('channel', ''));
  const [warrantyFilter, setWarrantyFilter] = useState(() => memory.initial('warranty', ''));
  const [fromDate, setFromDate] = useState(() => memory.initial('fromDate', ''));
  const [toDate, setToDate] = useState(() => memory.initial('toDate', ''));
  const [faultCategories, setFaultCategories] = useState([]);
  const [statusGroups, setStatusGroups] = useState(FALLBACK_STATUS_GROUPS);
  const [channelOptions, setChannelOptions] = useState(FALLBACK_CHANNEL_OPTIONS);

  useEffect(() => {
    const loadCategories = async () => {
      try {
        const response = await authFetch('/api/v1/support/fault-categories');
        if (response.ok) {
          const data = await response.json();
          setFaultCategories(data.data || []);
        }
      } catch (error) {
        console.error('Failed to load fault categories:', error);
      }
    };
    // Filter options mirror the configured support lists — only values that
    // can actually exist on new work are offered.
    const loadFilterOptions = async () => {
      try {
        const response = await authFetch('/api/v1/support/config-lists');
        if (response.ok) {
          const body = await response.json();
          setStatusGroups(buildStatusGroups(body.data));
          const channels = (body.data?.comms_channels || [])
            .filter(item => item.active !== false)
            .map(({ value, label }) => ({ value, label }));
          if (channels.length > 0) setChannelOptions(channels);
        }
      } catch (error) {
        console.error('Failed to load filter options:', error);
      }
    };
    loadCategories();
    loadFilterOptions();
  }, [authFetch]);

  const ticketFiltersActive = Boolean(faultCategory || channelFilter || warrantyFilter || fromDate || toDate);

  // Table, Board or Engineers (Gareth 2026-10-20): team leaders, managers,
  // admins and coordinators can switch to the board or the view by engineer;
  // everyone else keeps the table.
  const boardAllowed = canSeeWorkBoard(user?.role);
  const [view, setView] = useState(() => {
    const remembered = memory.initial('view', 'list');
    return ['board', 'engineers'].includes(remembered) ? remembered : 'list';
  });
  const showBoard = boardAllowed && (view === 'board' || view === 'engineers');

  useRememberList('/tickets', {
    view,
    search,
    tab: activeTab,
    status: statusFilter,
    my: myWork,
    faultCategory,
    channel: channelFilter,
    warranty: warrantyFilter,
    fromDate,
    toDate,
    page: pagination.page
  });

  const fetchWork = useCallback(async () => {
    const requestId = latestRequest.current + 1;
    latestRequest.current = requestId;
    setLoading(true);
    let steppingBack = false;
    try {
      const params = new URLSearchParams({
        page: String(pagination.page),
        limit: '25',
        work_type: activeTab,
        // '' = active work only; 'all' = every status (e.g. a date-range
        // sweep including closed tickets); anything else = that one status.
        active: statusFilter ? 'false' : 'true'
      });

      if (search) params.append('search', search);
      if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
      if (myWork) params.append('my', 'true');
      if (faultCategory) params.append('fault_category', faultCategory);
      if (channelFilter) params.append('channel', channelFilter);
      if (warrantyFilter) params.append('is_warranty', warrantyFilter);
      if (fromDate) params.append('from_date', fromDate);
      if (toDate) params.append('to_date', toDate);
      if (sort) {
        params.append('sort', sort.key);
        params.append('dir', sort.dir);
      }

      const response = await authFetch(`/api/v1/jobs?${params}`);
      const data = await response.json();
      if (requestId !== latestRequest.current) return;
      // A page past the end (the list got shorter while a job was open, and
      // the page was restored) steps back to the last page there is. The list
      // stays loading until that page arrives, so it never reads as empty.
      const lastPage = Math.max(1, data.pagination?.totalPages || 0);
      if (response.ok && data.pagination && pagination.page > lastPage) {
        steppingBack = true;
        setPagination(prev => ({ ...prev, page: lastPage }));
        return;
      }
      setItems(data.data || []);
      // The page stays the one asked for: writing the reply's page back would
      // start another fetch, and a late reply could bounce between two pages.
      setPagination(prev => ({
        ...prev,
        total: data.pagination?.total || 0,
        totalPages: data.pagination?.totalPages || 0
      }));
      setTypeCounts(data.meta?.type_counts || {});
    } catch (error) {
      console.error('Failed to fetch jobs and tickets:', error);
    } finally {
      if (requestId === latestRequest.current && !steppingBack) setLoading(false);
    }
  }, [authFetch, pagination.page, activeTab, statusFilter, myWork, search, faultCategory, channelFilter, warrantyFilter, fromDate, toDate, sort]);

  useEffect(() => {
    // The board reads its own cards.
    if (showBoard) return;
    fetchWork();
  }, [fetchWork, showBoard]);

  const resetPage = () => setPagination(prev => ({ ...prev, page: 1 }));

  const changeSort = (key) => {
    const next = nextSort(sort, key);
    resetPage();
    const params = new URLSearchParams(searchParams);
    if (next) {
      params.set('sort', next.key);
      params.set('dir', next.dir);
    } else {
      params.delete('sort');
      params.delete('dir');
    }
    setSearchParams(params, { replace: true });
  };

  // What pressing a header will do next, for its tooltip.
  const sortHint = (key, label) => {
    const next = nextSort(sort, key);
    if (!next) return 'Back to the usual order';
    return `Sort by ${label}, ${next.dir === 'asc' ? 'ascending' : 'descending'}`;
  };

  const handleSearch = (event) => {
    event.preventDefault();
    resetPage();
    fetchWork();
  };

  const getDetailPath = (item) => {
    if (item.work_type === 'reactive_ticket') return `/tickets/${item.id}`;
    if (item.work_type === 'scheduled_work') return `/tasks/${item.id}`;
    if (item.work_type === 'scheduled_maintenance') return `/maintenance/${item.id}`;
    return null;
  };

  const getWorkTypeLabel = (type) => {
    switch (type) {
      case 'reactive_ticket': return 'Reactive Ticket';
      case 'scheduled_work': return 'Scheduled Work';
      case 'scheduled_maintenance': return 'Scheduled Maintenance';
      default: return type;
    }
  };

  const getStatusClass = (status) => {
    switch (status) {
      case 'open': return 'status-open';
      case 'in_progress': return 'status-in-progress';
      case 'awaiting_customer':
      case 'awaiting_parts':
      case 'scheduled':
      case 'suggested':
      case 'confirmed':
        return 'status-awaiting';
      case 'overdue':
      case 'escalated':
        return 'status-escalated';
      case 'resolved':
      case 'complete':
      case 'completed':
        return 'status-resolved';
      case 'closed':
      case 'cancelled':
        return 'status-closed';
      default: return '';
    }
  };

  const getStatusLabel = (status) => {
    if (status === 'complete' || status === 'completed') return 'Completed';
    return String(status || '').replace(/_/g, ' ');
  };

  // Until a job is finished, the latest work-log outcome is the real state of
  // play (e.g. "parts required") — the raw status barely moves once work
  // starts. Finished work shows its final status as before.
  const FINISHED_STATUSES = ['closed', 'cancelled', 'resolved', 'complete', 'completed'];

  const renderStatus = (item) => {
    const reopenedChip = !FINISHED_STATUSES.includes(item.status) && item.reopened_at
      ? <span className="reopened-mini-chip" title="This ticket was reopened">Reopened</span>
      : null;
    if (!FINISHED_STATUSES.includes(item.status) && item.last_outcome) {
      return (
        <>
          <span
            className="status-badge status-outcome"
            title={`Status: ${getStatusLabel(item.status)} — latest work log outcome shown`}
          >
            {String(item.last_outcome).replace(/_/g, ' ')}
          </span>
          {reopenedChip}
        </>
      );
    }
    return (
      <>
        <span className={`status-badge ${getStatusClass(item.status)}`}>{getStatusLabel(item.status)}</span>
        {reopenedChip}
      </>
    );
  };

  const getPriorityClass = (priority) => {
    switch (priority) {
      case 'critical': return 'priority-critical';
      case 'high': return 'priority-high';
      case 'normal': return 'priority-normal';
      case 'low': return 'priority-low';
      default: return 'priority-low';
    }
  };

  // work_date mixes sources. A ticket's is its opened_at, an instant shown on
  // the viewer's own day. A task's is a DATE, which the API also sends as
  // work_day ('YYYY-MM-DD') so it reads as that calendar day in every zone.
  const formatWorkDate = (item) => {
    const options = { day: '2-digit', month: 'short', year: 'numeric' };
    if (item.work_type === 'reactive_ticket') {
      return item.work_date ? new Date(item.work_date).toLocaleDateString('en-GB', options) : '-';
    }
    return formatDateKey(item.work_day || item.work_date, options);
  };

  const formatTime = (time) => {
    if (!time) return '';
    return String(time).slice(0, 5);
  };

  return (
    <div className="tickets-page jobs-page">
      <div className="page-header">
        <div>
          <h1>Jobs &amp; Tickets</h1>
          <p>Central hub for reactive support, planned engineer work and scheduled maintenance</p>
        </div>
        <div className="jobs-header-actions">
          <Link to="/tickets/new" className="job-create-btn reactive">New Reactive Ticket</Link>
          {/* Team leaders and up book scheduled work (Gareth 2026-09-23). */}
          {getCapabilities(user).schedule_work && (
            <Link to="/tasks/new?work_type=engineer" className="job-create-btn scheduled">Schedule Engineer Work</Link>
          )}
        </div>
      </div>

      {boardAllowed && (
        <div className="jobs-view-switch" role="group" aria-label="Show the table, the board or the work by engineer">
          {[['list', 'Table'], ['board', 'Board'], ['engineers', 'Engineers']].map(([value, label]) => (
            <button
              key={value}
              type="button"
              className={view === value ? 'active' : ''}
              aria-pressed={view === value}
              onClick={() => setView(value)}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {showBoard ? (view === 'engineers' ? <EngineerBoard /> : <WorkBoard />) : (
      <>
      <div className="jobs-tabs">
        {WORK_TABS.map(tab => (
          <button
            key={tab.value}
            className={activeTab === tab.value ? 'active' : ''}
            onClick={() => {
              setActiveTab(tab.value);
              resetPage();
            }}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className="tickets-dashboard jobs-summary">
        <div className="stat-card">
          <div className="stat-label">Matching</div>
          <div className="stat-value">{(typeCounts.reactive_ticket || 0) + (typeCounts.scheduled_work || 0) + (typeCounts.scheduled_maintenance || 0)}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Reactive</div>
          <div className="stat-value">{typeCounts.reactive_ticket || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Scheduled Work</div>
          <div className="stat-value">{typeCounts.scheduled_work || 0}</div>
        </div>
        <div className="stat-card">
          <div className="stat-label">Maintenance</div>
          <div className="stat-value">{typeCounts.scheduled_maintenance || 0}</div>
        </div>
      </div>

      <div className="tickets-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search jobs, tickets, customers or assets..."
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-btn">Search</button>
        </form>
        <select
          value={statusFilter}
          onChange={(event) => {
            setStatusFilter(event.target.value);
            resetPage();
          }}
          className="filter-select"
        >
          <option value="">Active / Open</option>
          <option value="all">All Statuses</option>
          {statusGroups.map(group => (
            <optgroup key={group.label} label={group.label}>
              {group.options.map(option => (
                <option key={`${group.label}-${option.value}`} value={option.value}>{option.label}</option>
              ))}
            </optgroup>
          ))}
        </select>
        <label className="checkbox-filter">
          <input
            type="checkbox"
            checked={myWork}
            onChange={(event) => {
              setMyWork(event.target.checked);
              resetPage();
            }}
          />
          My Work
        </label>
      </div>

      <div className="tickets-filters ticket-only-filters">
        <select
          value={faultCategory}
          onChange={(event) => { setFaultCategory(event.target.value); resetPage(); }}
          className="filter-select"
        >
          <option value="">All Fault Categories</option>
          {faultCategories.map(cat => (
            <option key={cat.value} value={cat.value}>{cat.label}</option>
          ))}
        </select>
        <select
          value={channelFilter}
          onChange={(event) => { setChannelFilter(event.target.value); resetPage(); }}
          className="filter-select"
        >
          <option value="">All Channels</option>
          {channelOptions.map(option => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          value={warrantyFilter}
          onChange={(event) => { setWarrantyFilter(event.target.value); resetPage(); }}
          className="filter-select"
        >
          <option value="">Warranty &amp; Billable</option>
          <option value="true">Warranty Only</option>
          <option value="false">Non-warranty Only</option>
        </select>
        <input
          type="date"
          value={fromDate}
          onChange={(event) => { setFromDate(event.target.value); resetPage(); }}
          className="filter-select"
          title="Opened from"
        />
        <input
          type="date"
          value={toDate}
          onChange={(event) => { setToDate(event.target.value); resetPage(); }}
          className="filter-select"
          title="Opened to"
        />
        {ticketFiltersActive && (
          <>
            <span className="ticket-filter-note">Ticket filters apply - showing reactive tickets only</span>
            <button
              type="button"
              className="clear-filters-btn"
              onClick={() => {
                setFaultCategory(''); setChannelFilter(''); setWarrantyFilter('');
                setFromDate(''); setToDate(''); resetPage();
              }}
            >
              Clear
            </button>
          </>
        )}
      </div>

      {/* The message shows only on a first load. Once there are rows, a new
          sort, page or filter keeps the table on screen, dimmed, so the list
          does not flash and the header just pressed keeps the focus. */}
      {loading && items.length === 0 ? (
        <div className="loading">Loading work...</div>
      ) : (
        <>
          <div className={`tickets-table${loading ? ' is-reloading' : ''}`} aria-busy={loading}>
            <table>
              <thead>
                <tr>
                  {SORT_COLUMNS.map(([key, label]) => {
                    const active = sort?.key === key;
                    return (
                      <th key={key} aria-sort={active ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}>
                        <button
                          type="button"
                          className={`sort-header${active ? ' active' : ''}`}
                          onClick={() => changeSort(key)}
                          title={sortHint(key, label)}
                        >
                          {label}
                          <span className="sort-arrow" aria-hidden="true">
                            {active ? (sort.dir === 'asc' ? '\u25B2' : '\u25BC') : '\u2195'}
                          </span>
                        </button>
                      </th>
                    );
                  })}
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {items.length === 0 ? (
                  <tr>
                    <td colSpan="10" className="empty-row">No active work found</td>
                  </tr>
                ) : items.map(item => {
                  const detailPath = getDetailPath(item);
                  return (
                    <tr key={`${item.work_type}-${item.id}`} className={item.response_breached || item.resolution_breached || item.status === 'overdue' ? 'sla-breached' : ''}>
                      <td>
                        {detailPath ? (
                          <Link to={detailPath} className="ticket-ref">{item.ref}</Link>
                        ) : (
                          <span className="ticket-ref">{item.ref}</span>
                        )}
                      </td>
                      <td><span className={`work-type-chip ${item.work_type}`}>{getWorkTypeLabel(item.work_type)}</span></td>
                      <td className="subject-cell">
                        {detailPath ? (
                          <Link to={detailPath} className="subject-link">{item.title}</Link>
                        ) : (
                          <span className="subject-link static">{item.title}</span>
                        )}
                        {(item.response_breached || item.resolution_breached) && <span className="fault-tag">SLA breach</span>}
                        {item.previous_ticket_ref && (
                          <span className="recurrence-tag" title={`Follow-on of ${item.previous_ticket_ref}`}>
                            Follow-on of {item.previous_ticket_ref}
                          </span>
                        )}
                      </td>
                      <td>
                        {item.customer_id ? (
                          <Link to={`/customers/${item.customer_id}`} className="customer-link">{item.customer_name}</Link>
                        ) : '-'}
                      </td>
                      <td>
                        {item.asset_id ? (
                          <>
                            <Link to={`/assets/${item.asset_id}`} className="asset-link">{item.asset_ref}</Link>
                            {Number(item.asset_count) > 1 && (
                              <span className="asset-more" title={item.asset_refs || undefined}> +{Number(item.asset_count) - 1}</span>
                            )}
                          </>
                        ) : '-'}
                      </td>
                      <td>
                        {item.priority ? (
                          <span className={`priority-badge ${getPriorityClass(item.priority)}`}>{item.priority}</span>
                        ) : '-'}
                      </td>
                      <td>{renderStatus(item)}</td>
                      <td className="time-cell">
                        {formatWorkDate(item)}
                        {item.start_time && <small>{formatTime(item.start_time)}</small>}
                      </td>
                      <td>
                        {item.assigned_to_name || (item.assigned_to_planning_group ? 'Maintenance Planning Owner' : '-')}
                      </td>
                      <td>
                        {detailPath ? (
                          <Link to={detailPath} className="table-action-link">Open</Link>
                        ) : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 1 && (
            <div className="pagination">
              <button
                className="pagination-btn"
                disabled={loading || pagination.page === 1}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
              >
                Previous
              </button>
              <span className="pagination-info">Page {pagination.page} of {pagination.totalPages}</span>
              <button
                className="pagination-btn"
                disabled={loading || pagination.page === pagination.totalPages}
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
              >
                Next
              </button>
            </div>
          )}
        </>
      )}

      </>
      )}
    </div>
  );
}

export default Tickets;
